/*
 * groovi-confetti.js — celebration confetti on Add-to-Cart (2026-07-07)
 *
 * Fills the "empty button" flash — when a `.add-to-cart-button` enters its
 * `[data-added]` state the native CSS clips the label away and shows only a tiny
 * checkmark, which on a wide button reads as empty (a "feels broken" beat before
 * the cart drawer opens). This drops a confetti burst + a pixel checkmark OVER the
 * whole button, clipped to it, so something celebratory happens in that window.
 * Works site-wide via the shared `[data-added]` hook (main / quick / card /
 * cross-sell ATC buttons all use it) — the pixel checkmark fits every button size.
 *
 * Approach (proven on theme dev):
 *   - The confetti asset is a CSS-animated SVG (id-targeted @keyframes) — it only
 *     animates INLINE in the DOM (not as background-image / <img>), and `<use>` of
 *     a <symbol> does NOT animate (internal <style>/id rules don't scope into the
 *     use shadow tree). So each tile is a full raw inline copy; duplicate ids are
 *     tolerated (CSS @keyframes names are global, `#Frame_N` selectors match every
 *     tile), so all tiles animate in lockstep — a moving pattern.
 *   - RUN ONCE, not looping: the source loops `infinite`, but the button's added-
 *     state lifecycle awaits `onAnimationEnd` (getAnimations subtree), whose
 *     `.finished` promise never resolves for an infinite animation → `data-added`
 *     would never clear (overlay + button stuck). We force iteration-count:1 so it
 *     plays once, resolves, and the button restores normally.
 *
 * Tuning knobs: TILE_TARGET_PX (tile width → tile count) and SPEED_S.
 */

const SVG_URL = document.currentScript?.dataset.svg || '';
const TILE_TARGET_PX = 300; // each confetti tile ~this wide → fewer, larger tiles
const SPEED_S = 0.7; // confetti one-shot duration
const OVERLAY_TTL = 1400; // ms safety: self-remove the overlay if data-added hangs

// Pixel checkmark (from assets/icon-checkmark.svg — 1.5px rects), recoloured to
// currentColor so it inherits the button ink. Fits every button size.
const CHECK_SVG =
  '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">' +
  '<rect x="15" y="6.5" width="1.5" height="1.5"/><rect x="13.5" y="8" width="1.5" height="1.5"/>' +
  '<rect x="15" y="8" width="1.5" height="1.5"/><rect x="12" y="9" width="1.5" height="1.5"/>' +
  '<rect x="13.5" y="9" width="1.5" height="1.5"/><rect x="4.5" y="10.5" width="1.5" height="1.5"/>' +
  '<rect x="10.5" y="10.5" width="1.5" height="1.5"/><rect x="12" y="10.5" width="1.5" height="1.5"/>' +
  '<rect x="4.5" y="12" width="1.5" height="1.5"/><rect x="6" y="12" width="1.5" height="1.5"/>' +
  '<rect x="9" y="12" width="1.5" height="1.5"/><rect x="10.5" y="12" width="1.5" height="1.5"/>' +
  '<rect x="6" y="13.5" width="1.5" height="1.5"/><rect x="7.5" y="13.5" width="1.5" height="1.5"/>' +
  '<rect x="9" y="13.5" width="1.5" height="1.5"/><rect x="7.5" y="15" width="1.5" height="1.5"/></svg>';

/** @type {string | null} cached SVG markup with its <style> stripped out */
let svgMarkup = null;

/**
 * Lazy-fetch the confetti SVG once (on first intent). Extracts the SVG's internal
 * <style> (99 @keyframes + `#Frame_N { animation }` rules) and injects it into
 * <head> ONE TIME, then strips it from the markup so per-tile / per-click injection
 * never re-adds a <style>. A <style> insertion forces a document-wide style recalc,
 * and on a collection page that recalc reset the CSS hover-image swap mid-hover →
 * the "image flash" on ATC click. Injecting the keyframes once (pre-warmed on hover)
 * means clicks add zero stylesheets. The `#Frame_N` selectors are global, so they
 * still bind every tile's frames.
 */
async function loadSvg() {
  if (svgMarkup || !SVG_URL) return svgMarkup;
  try {
    const res = await fetch(SVG_URL);
    const raw = res.ok ? await res.text() : null;
    if (!raw) return (svgMarkup = null);
    if (!document.getElementById('groovi-confetti-keyframes')) {
      const css = [...raw.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)].map((m) => m[1]).join('\n');
      if (css) {
        const kf = document.createElement('style');
        kf.id = 'groovi-confetti-keyframes';
        kf.textContent = css;
        document.head.appendChild(kf);
      }
    }
    svgMarkup = raw.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  } catch (_) {
    svgMarkup = null;
  }
  return svgMarkup;
}

/**
 * Build the confetti + label overlay for one button and inject it.
 * @param {HTMLElement} button
 */
function celebrate(button) {
  if (!svgMarkup || button.querySelector(':scope > .groovi-confetti')) return;

  const width = button.getBoundingClientRect().width || 320;
  const tiles = Math.max(1, Math.min(8, Math.ceil(width / TILE_TARGET_PX)));

  const overlay = document.createElement('span');
  overlay.className = 'groovi-confetti';
  overlay.setAttribute('aria-hidden', 'true');

  const strip = document.createElement('span');
  strip.className = 'groovi-confetti__strip';
  for (let i = 0; i < tiles; i++) {
    const tile = document.createElement('span');
    tile.className = 'groovi-confetti__tile';
    // Raw copies (no id-namespacing): CSS @keyframes names are global and the
    // SVG's internal `#Frame_N { animation }` selectors match across every tile,
    // so all tiles animate. Duplicate ids are invalid HTML but browsers tolerate
    // them for CSS matching + url(#..) refs (all tiles share the first gradients,
    // which are identical anyway). Tiles animate in lockstep — reads as a moving
    // pattern, which is the intended "tiled" look.
    tile.innerHTML = svgMarkup;
    const svg = tile.querySelector('svg');
    if (svg) {
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    }
    strip.appendChild(tile);
  }
  overlay.appendChild(strip);

  // Pixel checkmark centered over the confetti (fits every button size — replaces
  // the too-wide "Adicionado!" text so small card / cross-sell buttons work too).
  const check = document.createElement('span');
  check.className = 'groovi-confetti__check';
  check.innerHTML = CHECK_SVG;
  overlay.appendChild(check);

  // Speed knob as an INLINE CSS var (not an injected <style> — a <style> insertion
  // forces a document-wide style recalc every click, which flashed the card images
  // on collection pages). The static rule in groovi-custom.css reads it + forces
  // RUN ONCE (iteration-count:1) — an infinite animation would hang the button's
  // onAnimationEnd (see file header). An inline prop change is scoped, not global.
  overlay.style.setProperty('--gc-speed', SPEED_S + 's');

  // Class (not a `:has()` selector) so hiding the native checkmark doesn't trigger a
  // broad `:has()` invalidation/recalc on every ATC button when the overlay mounts.
  button.classList.add('groovi-celebrating');
  button.appendChild(overlay);

  // Safety net: if `data-added` somehow doesn't clear (e.g. a third-party button),
  // remove the overlay after the burst so it never sticks.
  setTimeout(() => clear(button), OVERLAY_TTL);
}

/** Remove any overlay from a button. */
function clear(button) {
  button.classList.remove('groovi-celebrating');
  button.querySelector(':scope > .groovi-confetti')?.remove();
}

// Warm the asset on first pointer intent over any ATC button.
document.addEventListener(
  'pointerenter',
  (e) => {
    const t = e.target;
    if (t instanceof Element && t.closest('.add-to-cart-button')) loadSvg();
  },
  { capture: true }
);

// React to the shared [data-added] hook. One document observer, cheap (attribute
// filter). data-added=true → celebrate; removed → clear.
new MutationObserver((mutations) => {
  for (const m of mutations) {
    const el = m.target;
    if (!(el instanceof HTMLElement) || !el.classList.contains('add-to-cart-button')) continue;
    if (el.getAttribute('data-added') === 'true') {
      loadSvg().then(() => {
        // still added when the (cached) markup is ready?
        if (el.getAttribute('data-added') === 'true') celebrate(el);
      });
    } else {
      clear(el);
    }
  }
}).observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['data-added'],
  subtree: true,
});
